'use client';

/**
 * ErrorBoundary.tsx
 *
 * Root React error boundary. Catches render errors thrown anywhere in the
 * subtree and replaces the offending UI with a copy-able fallback that
 * shows the CRSC-XXXX code (when the error carries one), the request_id
 * if known, and a "Report bug" button that pre-fills the BugReportButton
 * dialog.
 *
 * Why a class component: React's Hooks API still has no equivalent of
 * componentDidCatch. Functional components can only catch errors in event
 * handlers and effect bodies, not during render.
 *
 * Why mounted at root layout: render errors below a deeper boundary
 * already get caught locally, but errors during the initial render of any
 * top-level page (e.g. a borked Settings tab) need a global safety net so
 * the whole tab doesn't go white.
 *
 * Behavior contract:
 *   1. On error: log to clientLogger.logError() with code/error_id from
 *      the thrown error if it's a CrewSyncError-shaped object.
 *   2. Render the fallback UI showing the code + Copy + Report bug buttons.
 *   3. The "Reload" button calls window.location.reload(); the boundary
 *      doesn't try to reset its own state via setState because by the time
 *      we're here, the tree under us is already corrupt.
 *
 * Additive only: existing UX is unaffected when nothing throws.
 */

import React from 'react';
import { logError } from '@/lib/client-logger';

type Props = {
  children: React.ReactNode;
  /** Optional override: callers can supply their own fallback UI. */
  fallback?: (info: { code: string | null; errorId: string | null; reset: () => void }) => React.ReactNode;
};

type State = {
  hasError: boolean;
  errorCode: string | null;
  errorId: string | null;
  errorMessage: string | null;
};

const INITIAL_STATE: State = {
  hasError: false,
  errorCode: null,
  errorId: null,
  errorMessage: null,
};

// CrewSyncError-shaped objects expose `code` and `errorId`. The backend
// error responses send the same shape via JSON, but a render-time crash
// won't have those — only thrown CrewSyncError-derived classes do.
function extractCode(err: unknown): string | null {
  if (!err || typeof err !== 'object') return null;
  const code = (err as { code?: unknown }).code;
  if (typeof code === 'string' && /^CRSC-\d{3,6}$/.test(code)) return code;
  return null;
}

function extractErrorId(err: unknown): string | null {
  if (!err || typeof err !== 'object') return null;
  const id = (err as { errorId?: unknown; error_id?: unknown }).errorId
    ?? (err as { error_id?: unknown }).error_id;
  if (typeof id === 'string' && id.length > 0) return id;
  return null;
}

export default class ErrorBoundary extends React.Component<Props, State> {
  state: State = INITIAL_STATE;

  static getDerivedStateFromError(err: unknown): Partial<State> {
    return {
      hasError: true,
      errorCode: extractCode(err),
      errorId: extractErrorId(err),
      errorMessage: err instanceof Error ? err.message : String(err),
    };
  }

  componentDidCatch(err: unknown, info: React.ErrorInfo) {
    // Fire-and-forget. logError swallows its own failures.
    void logError(err, {
      route: typeof window !== 'undefined' ? window.location.pathname : undefined,
      error_code: extractCode(err) || undefined,
      error_id: extractErrorId(err) || undefined,
    });
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary] caught:', err, info?.componentStack);
  }

  reset = () => {
    this.setState(INITIAL_STATE);
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    if (this.props.fallback) {
      return this.props.fallback({
        code: this.state.errorCode,
        errorId: this.state.errorId,
        reset: this.reset,
      });
    }
    return (
      <DefaultFallback
        code={this.state.errorCode}
        errorId={this.state.errorId}
        message={this.state.errorMessage}
      />
    );
  }
}

function DefaultFallback({
  code,
  errorId,
  message,
}: {
  code: string | null;
  errorId: string | null;
  message: string | null;
}) {
  const [copied, setCopied] = React.useState(false);

  const copyValue = [
    code ? `Code: ${code}` : null,
    errorId ? `Error ID: ${errorId}` : null,
    typeof window !== 'undefined' ? `URL: ${window.location.href}` : null,
    message ? `Detail: ${message}` : null,
  ].filter(Boolean).join(' | ');

  async function copy() {
    try {
      await navigator.clipboard.writeText(copyValue);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // navigator.clipboard not available (e.g. http context). Silent —
      // the copy button is a convenience, not load-bearing.
    }
  }

  // Open the existing bug-report modal by simulating a click on the
  // header bug button. The button has a stable data attribute so we
  // don't have to plumb a context provider just to fire the dialog.
  function reportBug() {
    if (typeof document === 'undefined') return;
    const btn = document.querySelector<HTMLButtonElement>('[data-bug-button]');
    if (btn) btn.click();
  }

  return (
    <div
      role="alert"
      className="flex min-h-[60vh] items-center justify-center p-6"
    >
      <div className="w-full max-w-md rounded-xl border border-red-200 bg-red-50 p-6 dark:border-red-900 dark:bg-red-900/20">
        <div className="mb-3 flex items-center gap-2 text-red-700 dark:text-red-400">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          <h2 className="text-lg font-semibold">Something went wrong.</h2>
        </div>
        <p className="mb-3 text-sm text-red-700/80 dark:text-red-300/80">
          The page hit an unexpected error. We&apos;ve logged it. You can copy
          the details below or send a bug report so we know exactly what
          happened.
        </p>
        {code && (
          <div className="mb-3 rounded-lg bg-white/60 p-2 font-mono text-xs dark:bg-black/30">
            <div>Code: <span className="font-semibold">{code}</span></div>
            {errorId && <div className="mt-0.5 break-all text-[11px] text-gray-500">ID: {errorId}</div>}
          </div>
        )}
        {!code && errorId && (
          <div className="mb-3 rounded-lg bg-white/60 p-2 font-mono text-[11px] text-gray-600 dark:bg-black/30 dark:text-gray-300">
            ID: {errorId}
          </div>
        )}
        <div className="flex flex-wrap gap-2">
          <button
            onClick={copy}
            className="rounded-lg border border-red-300 bg-white px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-100 dark:border-red-700 dark:bg-red-900/30 dark:text-red-300 dark:hover:bg-red-900/50"
          >
            {copied ? 'Copied' : 'Copy'}
          </button>
          <button
            onClick={reportBug}
            className="rounded-lg bg-[#00C2A8] px-3 py-1.5 text-xs font-medium text-white hover:bg-[#00a68f]"
          >
            Report bug
          </button>
          <button
            onClick={() => { if (typeof window !== 'undefined') window.location.reload(); }}
            className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:bg-transparent dark:text-gray-300 dark:hover:bg-gray-800"
          >
            Reload
          </button>
        </div>
      </div>
    </div>
  );
}
